import type { FeesMasterRowDto } from './feesMasterContracts'
import type { FeeDefinition, FeeDefinitionList } from '../types/feeTypes'
import {
  FEE_LIFECYCLE_STAGE,
  FEE_PARTY_ROLE,
  FEE_RECURRENCE,
  FEE_VALUE_TYPE,
} from '@shared/constants/fees'

export function parseFeeDefinition(dto: FeesMasterRowDto): FeeDefinition {
  return {
    id: String(dto.id ?? ''),
    marketId: dto.marketId ?? null,
    code: String(dto.code ?? ''),
    name: String(dto.name ?? ''),
    description: dto.description ?? null,
    lifecycleStage: dto.lifecycleStage ?? FEE_LIFECYCLE_STAGE.ONBOARDING,
    defaultValue: String(dto.defaultValue ?? '0'),
    valueType: dto.valueType ?? FEE_VALUE_TYPE.FLAT,
    valueBasis: dto.valueBasis ?? null,
    unitBasis: dto.unitBasis ?? null,
    paidByRole: dto.paidByRole ?? FEE_PARTY_ROLE.INVESTOR,
    paidToRole: dto.paidToRole ?? FEE_PARTY_ROLE.PLATFORM,
    recurrence: dto.recurrence ?? FEE_RECURRENCE.ONE_TIME,
    isVatApplicable: Boolean(dto.isVatApplicable),
    vatPercentage: dto.vatPercentage ?? null,
    isCustomisable: dto.isCustomisable !== false,
    validFrom: String(dto.validFrom ?? ''),
    validTo: dto.validTo ?? null,
    isActive: dto.isActive !== false,
    createdAt: String(dto.createdAt ?? ''),
    updatedAt: String(dto.updatedAt ?? ''),
  }
}

/**
 * Defensive against envelope variation: the response may arrive as the
 * `{ rows, rowsCount }` payload itself or wrapped by `TransformInterceptor` in
 * a `{ data }` envelope, and a plain array is accepted so a future flattening
 * of the endpoint does not blank the screen.
 */
export function parseFeeDefinitionList(data: unknown): FeeDefinitionList {
  const rows = extractRows(data)
  return { rows: rows.map(parseFeeDefinition), rowsCount: extractRowsCount(data) ?? rows.length }
}

function extractRows(data: unknown): FeesMasterRowDto[] {
  if (Array.isArray(data)) return data as FeesMasterRowDto[]
  const payload = unwrap(data)
  if (Array.isArray(payload)) return payload as FeesMasterRowDto[]
  const rows = (payload as { rows?: unknown } | null)?.rows
  return Array.isArray(rows) ? (rows as FeesMasterRowDto[]) : []
}

function extractRowsCount(data: unknown): number | null {
  const count = (unwrap(data) as { rowsCount?: unknown } | null)?.rowsCount
  return typeof count === 'number' ? count : null
}

function unwrap(data: unknown): unknown {
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  return record.data ?? record
}

export function parseFeeDefinitionRow(data: unknown): FeeDefinition {
  const payload = unwrap(data)
  return parseFeeDefinition((payload ?? {}) as FeesMasterRowDto)
}
